import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import { FiPlay, FiPause, FiClock, FiCalendar, FiUsers, FiLock, FiSearch, FiGrid, FiList,
  FiX, FiCheck, FiVideo,  FiBell, FiArrowRight,  FiAward, 
  FiRadio, FiCheckCircle, FiVideoOff, FiMic, FiMicOff, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiChevronDown, FiUser, FiFilter, FiTag, FiShield, FiInfo,
  FiVolume2, FiVolume1, FiVolumeX, FiMaximize, FiMinimize, FiRotateCcw, FiRotateCw, FiSliders, FiFastForward } from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { liveSessionAPI, userAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import { toast } from "react-toastify";
import { useCheckIn } from "../../context/CheckInContext";
import { HubConnectionBuilder } from "@microsoft/signalr";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  ivory: "#FFF8EE",  dark: "#1F1F1F",
  mid: "#555",       muted: "#999",
  light: "#CCC",     border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── TIER CONFIG ─────────────────────────────────────────────────────────────
const TIERS = {
  Free:     { rank: 0, color: "#888",    bg: "rgba(200,200,200,0.15)", border: "rgba(200,200,200,0.3)",   label: "Free Trial" },
  Basic:    { rank: 1, color: "#3E5C2E", bg: "rgba(107,138,90,0.12)",  border: "rgba(107,138,90,0.25)",   label: "Basic" },
  Standard: { rank: 2, color: "#C8660A", bg: "rgba(242,154,12,0.12)",  border: "rgba(242,154,12,0.25)",   label: "Standard" },
  Premium:  { rank: 3, color: "#C8660A", bg: "rgba(242,154,12,0.18)",  border: "rgba(242,154,12,0.35)",   label: "Premium" },
  Pro:      { rank: 4, color: "#27500A", bg: "rgba(107,138,90,0.20)",  border: "rgba(107,138,90,0.4)",    label: "Pro" },
};

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const normalizeTier = (t) => {
  if (!t) return "Free";
  const low = t.toLowerCase().trim();
  if (low.includes("pro") || low.includes("ytt") || low.includes("vip") || low.includes("diamond")) return "Pro";
  if (low.includes("premium") || low.includes("premiun")) return "Premium";
  if (low.includes("standard") || low.includes("silver")) return "Standard";
  if (low.includes("basic") || low.includes("bronze")) return "Basic";
  if (low.includes("free") || low.includes("trial")) return "Free";
  return t;
};

const CATEGORIES  = ["All", "Asana", "Pranayama", "Meditation", "Breathwork", "YTT"];
const TIER_FILTER = ["All Tiers", "Free", "Basic", "Standard", "Premium", "Pro"];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CAT_COLORS = {
  Asana: { from: "#F29A0C", to: "#E97818" },
  Pranayama: { from: "#0F6E56", to: "#1D9E75" },
  Meditation: { from: "#3C3489", to: "#534AB7" },
  YTT: { from: "#4E6845", to: "#6B8A5A" },
  Breathwork: { from: "#085041", to: "#0F6E56" },
};

const getTierRank = (tierName, packages = []) => {
  if (!tierName) return 0;
  const low = String(tierName).toLowerCase().trim();
  
  // Free Tier
  if (low === "free" || low === "free trial" || low === "trial" || low === "all" || low === "all users") return 0;

  // Canonical standard tiers
  if (low === "basic" || low === "basic plan" || low === "bronze") return 1;
  if (low === "standard" || low === "standard plan" || low === "silver") return 2;
  if (low === "premium" || low === "premium plan") return 3;
  if (low === "pro" || low === "pro plan" || low === "ytt" || low === "ytt batch") return 4;

  if (Array.isArray(packages) && packages.length > 0) {
    // 1. Exact match against DB packages
    const exactPkg = packages.find(p => {
      const nameLow = (p.name || "").toLowerCase().trim();
      const typeLow = (p.packageType || "").toLowerCase().trim();
      return nameLow === low || typeLow === low;
    });
    if (exactPkg && typeof exactPkg.accessLevel === "number" && !isNaN(exactPkg.accessLevel)) {
      return exactPkg.accessLevel;
    }

    // 2. Partial match against custom packages in DB
    const partialPkg = packages.find(p => {
      const nameLow = (p.name || "").toLowerCase().trim();
      const typeLow = (p.packageType || "").toLowerCase().trim();
      return (nameLow && nameLow.includes(low)) || (typeLow && typeLow.includes(low));
    });
    if (partialPkg && typeof partialPkg.accessLevel === "number" && !isNaN(partialPkg.accessLevel)) {
      return partialPkg.accessLevel;
    }
  }

  // 3. General keyword fallbacks
  if (low.includes("pro") || low.includes("ytt") || low.includes("vip")) return 4;
  if (low.includes("premium") || low.includes("gold")) return 3;
  if (low.includes("standard") || low.includes("silver")) return 2;
  if (low.includes("basic") || low.includes("bronze")) return 1;
  return 1;
};

export const canUserAccessContent = (contentTier, userTier, userRank = 0, packages = []) => {
  if (!contentTier) return true;
  const cTier = String(contentTier).toLowerCase().trim();
  if (cTier === "free" || cTier === "free trial" || cTier === "trial" || cTier === "all" || cTier === "all users") return true;

  const uTier = String(userTier || "").toLowerCase().trim();

  // 1. Direct tier match (e.g. user subscribed to Gold watching Gold)
  if (uTier && (uTier === cTier || uTier.includes(cTier) || cTier.includes(uTier))) {
    return true;
  }

  const requiredRank = getTierRank(contentTier, packages);

  // 2. Rank comparison (User rank >= required content rank grants access)
  return userRank >= requiredRank;
};

let CURRENT_USER_TIER = "Free";
let CURRENT_USER_RANK = 0;
let CACHED_PACKAGES = [];

function userCanAccess(sessionTier) {
  return canUserAccessContent(sessionTier, CURRENT_USER_TIER, CURRENT_USER_RANK, CACHED_PACKAGES);
}

const parseUtcDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
    return new Date(dateStr + "Z");
  }
  return new Date(dateStr);
};

function formatCountdown(targetDate) {
  const diff = Math.max(0, targetDate - Date.now());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0)  return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

function formatTime(date) {
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatSessionTimeWithZone(date) {
  const localTimeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  let localTz = "";
  try {
    const parts = new Intl.DateTimeFormat([], { timeZoneName: "short" }).formatToParts(date);
    const tzPart = parts.find(p => p.type === "timeZoneName");
    if (tzPart) localTz = tzPart.value;
  } catch (e) {
    console.error(e);
  }

  const istTimeStr = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).format(date) + " IST";

  if (localTz && localTz !== "GMT+5:30" && localTz !== "IST") {
    return `${localTimeStr} ${localTz} (${istTimeStr})`;
  }
  return `${localTimeStr} IST`;
}

function formatDate(date) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(date); d.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric" });
}

function capacity(session) {
  const pct = Math.round((session.enrolled / session.capacity) * 100);
  if (pct >= 90) return { label: "Almost Full", color: "#E97818" };
  if (pct >= 70) return { label: "Filling Up",  color: T.orange };
  return { label: "Seats Open", color: T.olive };
}

// ─── TILT CARD ────────────────────────────────────────────────────────────────
function TiltCard({ children, style = {}, className = "", intensity = 7 }) {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [intensity, -intensity]), { stiffness: 260, damping: 28 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-intensity, intensity]), { stiffness: 260, damping: 28 });
  return (
    <motion.div ref={ref}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900, ...style }}
      className={className}
      onMouseMove={e => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5); }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >{children}</motion.div>
  );
}

// ─── COUNTDOWN TICKER ─────────────────────────────────────────────────────────
function Countdown({ targetDate, style = {} }) {
  const [tick, setTick] = useState(formatCountdown(targetDate));
  useEffect(() => {
    const t = setInterval(() => setTick(formatCountdown(targetDate)), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return <span style={style}>{tick}</span>;
}

// ─── TIER BADGE ───────────────────────────────────────────────────────────────
function TierBadge({ tier, size = "sm" }) {
  const normKey = normalizeTier(tier);
  const t = TIERS[tier] || TIERS[normKey] || { rank: 1, color: "#C8660A", bg: "rgba(242,154,12,0.12)", border: "rgba(242,154,12,0.28)", label: tier || "Free" };
  const p = size === "sm" ? "3px 10px" : "5px 14px";
  const fs = size === "sm" ? 10 : 12;
  const displayLabel = tier || t.label || "Free";
  return (
    <span style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}`,
      fontSize: fs, fontWeight: 700, padding: p, borderRadius: 100, display: "inline-flex",
      alignItems: "center", gap: 4 }}>
      {!userCanAccess(tier) && <FiLock size={9} />}
      {displayLabel}
    </span>
  );
}

// ─── CUSTOM DROPDOWN COMPONENT ────────────────────────────────────────────────
const CustomDropdown = ({ value, onChange, options, label, icon: Icon, fullWidth = false, className = "" }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOptionObj = options.find(opt => typeof opt === "object" ? opt.value === value : opt === value);
  let displayLabel = "";

  if (value === "All Tiers" || value === "all") {
    displayLabel = `All ${label}s`;
  } else if (value === "All Instructors") {
    displayLabel = "All Instructors";
  } else if (selectedOptionObj && typeof selectedOptionObj === "object") {
    displayLabel = selectedOptionObj.label;
  } else if (value) {
    displayLabel = value;
  } else {
    displayLabel = `All ${label}s`;
  }

  return (
    <div className={`relative ${fullWidth ? "w-full" : "inline-block min-w-[160px]"} ${className}`} ref={dropdownRef}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.985 }}
        onClick={() => setOpen(o => !o)}
        className={`px-4 py-2 font-bold text-[12px] flex items-center justify-between gap-2.5 cursor-pointer transition-all border outline-none ${
          fullWidth ? "w-full h-11 rounded-[14px]" : "h-9 rounded-full"
        }`}
        style={{
          background: open ? "#FFFFFF" : "rgba(255,255,255,0.92)",
          backdropFilter: "blur(16px)",
          borderColor: open ? "#F29A0C" : "rgba(239,229,216,0.95)",
          color: value && value !== "All Tiers" && value !== "All Instructors" && value !== "all" ? "#1F1F1F" : "#71717A",
          boxShadow: open 
            ? "0 0 0 3px rgba(242,154,12,0.18), 0 8px 24px rgba(0,0,0,0.06)" 
            : "0 2px 8px rgba(0,0,0,0.03)",
          fontFamily: "'Inter', sans-serif"
        }}>
        <div className="flex items-center gap-2 min-w-0 truncate">
          {Icon && <Icon size={14} className={`shrink-0 ${open ? "text-amber-600" : "text-amber-500"}`}/>}
          <span className="truncate">{displayLabel}</span>
        </div>
        <FiChevronDown size={14} className={`text-zinc-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-amber-600" : ""}`}/>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={`absolute left-0 z-[250] p-1.5 rounded-[18px] border overflow-hidden ${
              fullWidth ? "right-0 min-w-[220px]" : "min-w-[190px]"
            } top-[42px] max-h-[240px] overflow-y-auto [&::-webkit-scrollbar]:hidden`}
            style={{
              background: "#FFFFFF",
              backdropFilter: "blur(24px)",
              borderColor: "rgba(239,229,216,0.95)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.16)",
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }}>
            {options.map(opt => {
              const optVal = typeof opt === "object" ? opt.value : opt;
              const optLabel = typeof opt === "object" ? opt.label : opt;
              const isSelected = value === optVal;

              return (
                <button
                  key={optVal}
                  type="button"
                  onClick={() => {
                    onChange(optVal);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3.5 py-2 rounded-[12px] text-[12px] font-bold flex items-center justify-between cursor-pointer transition-all border-none my-0.5 ${
                    isSelected 
                      ? "bg-amber-500 text-white shadow-sm" 
                      : "text-zinc-700 hover:bg-amber-50 hover:text-amber-900"
                  }`}
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  <span className="truncate">{optLabel}</span>
                  {isSelected && <FiCheckCircle size={13} className="text-white shrink-0 ml-2"/>}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ session, onClose, packages = [] }) {
  const navigate = useNavigate();
  const tierName = session?.tier || "Standard";

  // Find dynamic package configured by Admin:
  const matchedPkg = useMemo(() => {
    if (!packages || packages.length === 0) return null;
    const low = String(tierName).toLowerCase().trim();
    return packages.find(p => {
      const pName = (p.name || "").toLowerCase().trim();
      const pType = (p.packageType || "").toLowerCase().trim();
      return pName === low || pType === low || pName.includes(low) || low.includes(pName);
    });
  }, [packages, tierName]);

  const tierLabel = matchedPkg?.name || session?.tier || "Membership";
  const catC = CAT_COLORS[session?.category] || CAT_COLORS.Asana;

  // Real admin description:
  const adminDescription = matchedPkg?.description || 
    `"${session?.title || 'This class'}" is available exclusively for ${tierLabel} members and above. Upgrade your plan to get full live streaming and recorded class access.`;

  // Real dynamic package bullet points (parsed from admin FeaturesJson):
  const perks = useMemo(() => {
    if (matchedPkg?.featuresJson) {
      try {
        const parsed = JSON.parse(matchedPkg.featuresJson);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter(Boolean);
        }
      } catch (e) {
        if (typeof matchedPkg.featuresJson === "string") {
          return matchedPkg.featuresJson.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
        }
      }
    }
    // Fallback baseline perks if admin hasn't filled features yet:
    return [
      `Full access to all ${tierLabel} live interactive classes`,
      "Priority booking and seat reservations before general enrollment",
      session?.recorded ? "Full HD recorded session replay available anytime" : "Interactive real-time audio & video access",
      "Direct instructor Q&A and personalized posture feedback",
      "Access to complete yoga curriculum and practice guides"
    ];
  }, [matchedPkg, tierLabel, session?.recorded]);

  const priceText = matchedPkg?.discountedPrice 
    ? ` (₹${matchedPkg.discountedPrice.toLocaleString()})`
    : matchedPkg?.price 
      ? ` (₹${matchedPkg.price.toLocaleString()})`
      : "";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(14px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.85, y: 28 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.85, y: 28 }} transition={{ type: "spring", stiffness: 320, damping: 30 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-[32px] overflow-hidden"
        style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.45)", background: "#fff" }}>

        {/* Header gradient */}
        <div className="relative p-8 text-white"
          style={{ background: `linear-gradient(145deg,${catC.from},${catC.to})` }}>
          <div className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "18px 18px" }} />
          <button onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center border-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>
            <FiX size={15} />
          </button>
          <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mb-5"
            style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
            <FiLock size={22} />
          </div>
          <h2 className="font-black text-[22px] mb-2" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            {tierLabel} Plan Required
          </h2>
          <p className="text-white/90 text-[13px] leading-[1.7]">
            {adminDescription}
          </p>
        </div>

        {/* Body */}
        <div style={{ background: "#fff", padding: "24px 28px" }}>
          <p className="text-[12px] font-bold uppercase tracking-[1px] mb-4 text-zinc-400">
            What you unlock with {tierLabel}
          </p>
          <div className="space-y-3 mb-6">
            {perks.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg,${catC.from},${catC.to})` }}>
                  <FiCheck size={10} color="#fff" />
                </div>
                <span className="text-[13px] text-zinc-700 font-medium">{item}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              onClick={() => {
                onClose();
                navigate("/user/settings?tab=subscription");
              }}
              className="flex-1 h-12 rounded-full font-bold text-[14px] text-white flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`,
                boxShadow: `0 10px 28px rgba(242,154,12,0.38)`, border: "none", cursor: "pointer",
                fontFamily: "'Inter',sans-serif" }}>
              Upgrade to {tierLabel}{priceText} <FiArrowRight style={{ display: "inline", marginLeft: 6 }} />
            </motion.button>
            <button onClick={onClose}
              className="h-12 px-5 rounded-full font-semibold text-[13px]"
              style={{ background: "rgba(200,200,200,0.12)", border: `1px solid ${T.border}`,
                color: T.muted, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              Later
            </button>
          </div>

          <p className="text-center text-[11px] mt-4 text-zinc-400">
            Cancel anytime · Instant access · All plans include full member benefits
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function RescheduleModal({ session, sessions, onClose, onConfirm }) {
  // Find other upcoming sessions of equal/lower tier that the user is not already checked in to
  const otherUpcoming = sessions.filter(s => 
    s.id !== session.id && 
    s.status === "upcoming" && 
    !s.isCheckedIn &&
    userCanAccess(s.tier)
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg rounded-[32px] overflow-hidden"
        style={{ background: "#fff", boxShadow: "0 32px 80px rgba(0,0,0,0.4)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        
        <div className="p-7 text-center border-b" style={{ borderColor: T.border }}>
          <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-3">
            <FiRefreshCw size={20} className="text-zinc-600 animate-spin-slow" />
          </div>
          <h2 className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>
            Reschedule Booking
          </h2>
          <p className="text-[12px] text-zinc-500 mt-1">
            Rescheduling from <strong className="text-zinc-700">"{session.title}"</strong>
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3 scrollbar-thin" style={{ maxHeight: 320 }}>
          {otherUpcoming.length === 0 ? (
            <div className="text-center py-8 text-zinc-400 text-xs font-semibold">
              No alternative upcoming classes available at this time.
            </div>
          ) : (
            otherUpcoming.map(s => (
              <div key={s.id} 
                className="p-4 rounded-2xl border border-zinc-100 hover:border-[#F29A0C]/40 hover:bg-[#F29A0C]/5 transition-all cursor-pointer flex items-center justify-between gap-4"
                onClick={() => onConfirm(s.id)}
              >
                <div className="min-w-0">
                  <h4 className="font-bold text-xs text-zinc-800 truncate">{s.title}</h4>
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    with {s.instructor} · {formatDate(s.start)} at {formatSessionTimeWithZone(s.start)}
                  </p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-[9px] rounded font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600">
                    {s.category}
                  </span>
                </div>
                <button 
                  className="px-3.5 py-1.5 rounded-full bg-[#F29A0C] text-white font-bold text-[10px] uppercase border-none cursor-pointer hover:bg-[#E97818] transition-colors"
                >
                  Switch
                </button>
              </div>
            ))
          )}
        </div>

        <div className="p-4 bg-zinc-50/50 border-t flex justify-end gap-3" style={{ borderColor: T.border }}>
          <button onClick={onClose} className="px-5 h-10 rounded-full font-bold text-[12px] text-zinc-500 hover:bg-zinc-100 border-none cursor-pointer transition-colors">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ReplayPlayerModal({ session, onClose }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const speedMenuRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const getFallbackDuration = () => {
    const raw = session?.duration || session?.durationInMinutes;
    const mins = typeof raw === "number" ? raw : parseInt(raw, 10);
    return !isNaN(mins) && isFinite(mins) && mins > 0 ? mins * 60 : 3600;
  };

  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(getFallbackDuration);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPos, setHoverPos] = useState(0);
  const [centerFeedback, setCenterFeedback] = useState(null);

  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const formatMediaTime = (secs) => {
    if (secs === null || secs === undefined || isNaN(secs) || !isFinite(secs) || secs < 0) {
      return "00:00";
    }
    const totalSecs = Math.floor(secs);
    if (!isFinite(totalSecs)) return "00:00";
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target)) {
        setShowSpeedMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
    };
  }, []);

  const resetControlsTimeout = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        if (!showSpeedMenu && !isScrubbing) {
          setShowControls(false);
        }
      }, 3500);
    }
  };

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying, showSpeedMenu, isScrubbing]);

  const triggerFeedback = (type) => {
    setCenterFeedback(type);
    setTimeout(() => setCenterFeedback(null), 600);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
      triggerFeedback("play");
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      triggerFeedback("pause");
    }
  };

  const effectiveDuration = (typeof duration === "number" && !isNaN(duration) && isFinite(duration) && duration > 0 && duration !== Infinity)
    ? duration
    : getFallbackDuration();

  const handleSkip = (seconds) => {
    if (!videoRef.current) return;
    const curr = (typeof videoRef.current.currentTime === "number" && isFinite(videoRef.current.currentTime))
      ? videoRef.current.currentTime
      : 0;
    const newTime = Math.max(0, Math.min(effectiveDuration, curr + seconds));
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    triggerFeedback(seconds > 0 ? "+10s" : "-10s");
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackRate(speed);
      setShowSpeedMenu(false);
      triggerFeedback(`${speed}x`);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.muted = false;
      setIsMuted(false);
      if (volume === 0) {
        setVolume(0.5);
        videoRef.current.volume = 0.5;
      }
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isScrubbing) {
      const curr = videoRef.current.currentTime;
      if (typeof curr === "number" && !isNaN(curr) && isFinite(curr) && curr >= 0) {
        setCurrentTime(curr);
      }
      const dur = videoRef.current.duration;
      if (typeof dur === "number" && !isNaN(dur) && isFinite(dur) && dur > 0 && dur !== Infinity) {
        setDuration(dur);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      if (typeof dur === "number" && !isNaN(dur) && isFinite(dur) && dur > 0 && dur !== Infinity) {
        setDuration(dur);
      }
      videoRef.current.playbackRate = playbackRate;
    }
  };

  const calculateSeekTime = (clientX) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pos * effectiveDuration;
  };

  const handleProgressMouseDown = (e) => {
    e.preventDefault();
    setIsScrubbing(true);
    const seekTime = calculateSeekTime(e.clientX);
    setCurrentTime(seekTime);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
    }

    const handleMouseMove = (moveEvent) => {
      const time = calculateSeekTime(moveEvent.clientX);
      setCurrentTime(time);
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    };

    const handleMouseUp = () => {
      setIsScrubbing(false);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleProgressHover = (e) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverPos(pos * 100);
    setHoverTime(pos * effectiveDuration);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.code === "Space" || e.key === "k") {
        e.preventDefault();
        togglePlay();
      } else if (e.code === "ArrowLeft" || e.key === "j") {
        e.preventDefault();
        handleSkip(-10);
      } else if (e.code === "ArrowRight" || e.key === "l") {
        e.preventDefault();
        handleSkip(10);
      } else if (e.code === "KeyF") {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.code === "KeyM") {
        e.preventDefault();
        toggleMute();
      } else if (e.code === "Escape") {
        if (!document.fullscreenElement) {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPlaying, effectiveDuration, isMuted, volume]);

  const progressPercent = effectiveDuration > 0
    ? Math.min(100, Math.max(0, (currentTime / effectiveDuration) * 100))
    : 0;

  const displayDurationMinutes = session?.duration || session?.durationInMinutes || Math.round(effectiveDuration / 60) || 60;

  return createPortal(
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(20px)" }}
      onClick={onClose}
    >
      <motion.div 
        ref={containerRef}
        initial={{ scale: 0.94, y: 20 }} 
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94 }} 
        transition={{ type: "spring", stiffness: 340, damping: 28 }}
        onClick={e => e.stopPropagation()}
        onMouseMove={resetControlsTimeout}
        className="w-full max-w-5xl rounded-[28px] md:rounded-[32px] overflow-hidden bg-zinc-950 border border-white/10 shadow-2xl flex flex-col relative select-none"
      >
        {/* Top Header Bar */}
        <div className="px-5 py-3.5 md:px-6 md:py-4 flex items-center justify-between border-b border-white/10 bg-zinc-900/90 backdrop-blur-md z-30">
          <div className="flex items-center gap-3 min-w-0 pr-4">
            <div className="w-2.5 h-7 rounded-full bg-gradient-to-b from-[#F29A0C] to-[#E97818] shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-sm md:text-base text-white truncate" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {session.title}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 shrink-0">
                  {session.category || "Recorded"}
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 mt-0.5 truncate">
                Instructor: <strong className="text-zinc-200">{session.instructor}</strong> · Total Duration: <strong className="text-amber-400">{displayDurationMinutes} mins</strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button 
              onClick={onClose} 
              aria-label="Close recorded player"
              className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border-none cursor-pointer transition-colors"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Video Player Display */}
        <div 
          className="relative aspect-video bg-black flex items-center justify-center overflow-hidden group"
          onClick={togglePlay}
          onDoubleClick={toggleFullscreen}
        >
          <video 
            ref={videoRef}
            src={session.videoUrl} 
            autoPlay 
            playsInline
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => setIsBuffering(false)}
            onCanPlay={() => setIsBuffering(false)}
            onEnded={() => {
              setIsPlaying(false);
              setShowControls(true);
            }}
            className="w-full h-full object-contain cursor-pointer"
          />

          {/* Buffering Indicator */}
          {isBuffering && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-[2px] z-20 pointer-events-none">
              <div className="w-12 h-12 rounded-full border-3 border-amber-500/20 border-t-amber-500 animate-spin mb-3" />
              <span className="text-xs font-bold text-white tracking-wider uppercase">Loading Stream...</span>
            </div>
          )}

          {/* Center Feedback Animation */}
          <AnimatePresence>
            {centerFeedback && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1.1 }}
                exit={{ opacity: 0, scale: 1.3 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
              >
                <div className="w-20 h-20 rounded-full bg-black/75 border border-white/20 backdrop-blur-md flex items-center justify-center text-white shadow-2xl">
                  {centerFeedback === "play" && <FiPlay size={32} className="ml-1 text-amber-400" />}
                  {centerFeedback === "pause" && <FiPause size={32} className="text-amber-400" />}
                  {centerFeedback === "+10s" && <span className="font-black text-sm text-amber-400">+10s</span>}
                  {centerFeedback === "-10s" && <span className="font-black text-sm text-amber-400">-10s</span>}
                  {centerFeedback.includes("x") && <span className="font-black text-sm text-amber-400">{centerFeedback}</span>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hover / Active Controls Overlay */}
          <div 
            onClick={e => e.stopPropagation()} 
            className={`absolute inset-x-0 bottom-0 pt-16 pb-3 px-4 md:px-6 bg-gradient-to-t from-black/95 via-black/70 to-transparent transition-opacity duration-300 flex flex-col justify-end z-25 ${
              showControls ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
          >
            {/* Interactive Duration & Progress Bar */}
            <div 
              ref={progressBarRef}
              onMouseDown={handleProgressMouseDown}
              onMouseMove={handleProgressHover}
              onMouseLeave={() => setHoverTime(null)}
              className="relative w-full py-2 cursor-pointer group/progress mb-2"
            >
              {/* Tooltip on hover */}
              {hoverTime !== null && (
                <div 
                  className="absolute -top-7 transform -translate-x-1/2 px-2 py-0.5 rounded bg-amber-500 text-black font-black text-[10px] shadow-lg pointer-events-none whitespace-nowrap z-30"
                  style={{ left: `${hoverPos}%` }}
                >
                  {formatMediaTime(hoverTime)}
                </div>
              )}

              {/* Bar background */}
              <div className="w-full h-1.5 group-hover/progress:h-2.5 bg-white/20 rounded-full overflow-hidden transition-all duration-150 relative">
                {/* Played progress fill */}
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full relative"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              {/* Scrubber Knob */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_10px_rgba(242,154,12,0.8)] border-2 border-amber-500 opacity-0 group-hover/progress:opacity-100 transition-opacity pointer-events-none"
                style={{ left: `${progressPercent}%` }}
              />
            </div>

            {/* Bottom Controls Row */}
            <div className="flex items-center justify-between gap-2 text-white">
              {/* Left group */}
              <div className="flex items-center gap-2 md:gap-3">
                {/* Play / Pause button */}
                <button
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-amber-500 hover:text-black text-white flex items-center justify-center border-none cursor-pointer transition-all shrink-0"
                >
                  {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} className="ml-0.5" />}
                </button>

                {/* Rewind -10s */}
                <button
                  onClick={() => handleSkip(-10)}
                  title="Rewind 10 seconds"
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 text-white/90 flex items-center justify-center border-none cursor-pointer transition-colors text-[11px] font-bold"
                >
                  <FiRotateCcw size={14} />
                </button>

                {/* Forward +10s */}
                <button
                  onClick={() => handleSkip(10)}
                  title="Forward 10 seconds"
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 text-white/90 flex items-center justify-center border-none cursor-pointer transition-colors text-[11px] font-bold"
                >
                  <FiRotateCw size={14} />
                </button>

                {/* Volume & Mute */}
                <div className="flex items-center gap-1.5 group/vol">
                  <button
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    className="w-8 h-8 rounded-full bg-transparent hover:bg-white/10 text-white/90 flex items-center justify-center border-none cursor-pointer transition-colors"
                  >
                    {isMuted || volume === 0 ? <FiVolumeX size={16} className="text-red-400" /> : volume < 0.5 ? <FiVolume1 size={16} /> : <FiVolume2 size={16} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-14 md:w-20 h-1 accent-amber-500 cursor-pointer bg-white/20 rounded-lg appearance-none"
                  />
                </div>

                {/* Duration / Current Time Display */}
                <div className="flex items-center gap-1.5 text-xs font-mono font-bold bg-white/5 px-2.5 py-1 rounded-lg border border-white/10 shrink-0">
                  <span className="text-amber-400">{formatMediaTime(currentTime)}</span>
                  <span className="text-white/40">/</span>
                  <span className="text-white/80">{formatMediaTime(effectiveDuration)}</span>
                </div>
              </div>

              {/* Right group */}
              <div className="flex items-center gap-2 md:gap-3 relative">
                {/* Speed Selector Button & Popover Menu */}
                <div className="relative" ref={speedMenuRef}>
                  <button
                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                    className="h-8 px-2.5 md:px-3 rounded-xl bg-white/10 hover:bg-amber-500 hover:text-black text-white font-bold text-[11px] md:text-xs flex items-center gap-1.5 border border-white/10 cursor-pointer transition-all"
                  >
                    <FiFastForward size={12} />
                    <span>{playbackRate}x</span>
                  </button>

                  <AnimatePresence>
                    {showSpeedMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        className="absolute bottom-11 right-0 bg-zinc-900/95 backdrop-blur-xl border border-white/15 rounded-2xl p-1.5 shadow-2xl z-40 min-w-[130px] flex flex-col gap-0.5"
                      >
                        <div className="px-2.5 py-1 text-[10px] font-black uppercase text-zinc-400 tracking-wider border-b border-white/10 mb-1">
                          Playback Speed
                        </div>
                        {speeds.map(s => (
                          <button
                            key={s}
                            onClick={() => handleSpeedChange(s)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold text-left flex items-center justify-between border-none cursor-pointer transition-colors ${
                              playbackRate === s 
                                ? "bg-amber-500 text-black" 
                                : "text-zinc-200 hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            <span>{s === 1 ? "1.0x (Normal)" : `${s}x`}</span>
                            {playbackRate === s && <FiCheck size={12} className="text-black shrink-0" />}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Fullscreen Button */}
                <button
                  onClick={toggleFullscreen}
                  title="Toggle Fullscreen (F)"
                  className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border-none cursor-pointer transition-colors shrink-0"
                >
                  {isFullscreen ? <FiMinimize size={16} /> : <FiMaximize size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer info & shortcut guide */}
        <div className="px-5 py-3 md:px-6 md:py-3.5 bg-zinc-900/90 border-t border-white/10 flex items-center justify-between text-[11px] text-zinc-400 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            <span>Class recorded and processed in HD</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[10px] text-zinc-400">
            <span><strong className="text-zinc-300">Space:</strong> Play/Pause</span>
            <span><strong className="text-zinc-300">← / →:</strong> -10s / +10s</span>
            <span><strong className="text-zinc-300">F:</strong> Fullscreen</span>
            <span><strong className="text-zinc-300">M:</strong> Mute</span>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function CancelConfirmModal({ session, onClose, onConfirm }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm rounded-[32px] overflow-hidden"
        style={{ background: "#fff", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}>
        
        <div className="p-7 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5 border border-red-100">
            <FiX size={28} className="text-red-500" />
          </div>
          
          <h2 className="font-black text-[20px] mb-2 text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            Cancel Booking Slot
          </h2>
          
          <p className="text-[13px] leading-[1.7] mb-6 text-zinc-500">
            Are you sure you want to cancel your slot booking for <strong className="text-zinc-800">"{session.title}"</strong>?
          </p>
          
          <div className="flex flex-col gap-2">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
              onClick={onConfirm}
              className="w-full h-12 rounded-full font-bold text-[14px] text-white"
              style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif", boxShadow: "0 8px 20px rgba(239,68,68,0.25)" }}
            >
              Yes, Cancel Slot
            </motion.button>
            
            <button onClick={onClose} 
              className="w-full h-12 rounded-full font-semibold text-[13px] text-zinc-500 hover:bg-zinc-100 transition-colors"
              style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
            >
              No, Keep Slot
            </button>
          </div>
        </div>
        
      </motion.div>
    </motion.div>
  );
}

// ─── CHECK-IN MODAL ───────────────────────────────────────────────────────────
function CountdownModal({ session, onClose, onStart }) {
  const catC = CAT_COLORS[session.category] || CAT_COLORS.Asana;
  
  const calculateTimeLeft = () => {
    const diff = new Date(session.start) - new Date();
    if (diff <= 0) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      total: diff,
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60)
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    if (timeLeft.total <= 0) {
      onStart();
      return;
    }
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining.total <= 0) {
        clearInterval(timer);
        onStart();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft.total]);

  const pad = (num) => String(num).padStart(2, "0");

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-[32px] overflow-hidden"
        style={{ background: "#fff", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}>
        
        {/* Top Header Card Background */}
        <div className="p-7 text-center relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${catC.from}, ${catC.to})` }}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent)] pointer-events-none" />
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-4 border border-white/10">
            <FiClock size={24} className="text-white" />
          </div>
          <h2 className="font-black text-[22px] text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "-0.5px" }}>
            Class Starts Soon
          </h2>
          <p className="text-[12px] text-white/80 font-medium">
            {session.title} · {session.instructor}
          </p>
        </div>

        {/* Time Counters Grid */}
        <div className="p-7 text-center">
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: "Days", val: timeLeft.days },
              { label: "Hours", val: timeLeft.hours },
              { label: "Mins", val: timeLeft.minutes },
              { label: "Secs", val: timeLeft.seconds }
            ].map((t, idx) => (
              <div key={idx} className="bg-zinc-50 border border-zinc-100 rounded-2xl p-3 flex flex-col items-center">
                <span className="text-[26px] font-black text-zinc-800 leading-none mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {pad(t.val)}
                </span>
                <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider">
                  {t.label}
                </span>
              </div>
            ))}
          </div>

          {/* Status Message */}
          <div className="flex items-start gap-3 text-left bg-[#F29A0C]/5 border border-[#F29A0C]/12 p-4 rounded-2xl mb-6">
            <FiRadio className="text-[#F29A0C] animate-pulse flex-shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-[12.5px] font-bold text-zinc-800 mb-0.5">Automated Classroom Redirect</p>
              <p className="text-[11.5px] text-zinc-500 leading-relaxed font-medium">
                You can join once the class begins live. This page will automatically redirect you into the live room as soon as the timer reaches zero.
              </p>
            </div>
          </div>

          <button onClick={onClose}
            className="w-full h-12 rounded-full font-bold text-[13px] bg-zinc-900 text-white hover:bg-zinc-800 transition-colors border-none cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Close Window
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── SESSION CARD ─────────────────────────────────────────────────────────────
function SessionCard({ session, delay = 0, onJoin, onUpgrade, onCancelBooking, onReschedule, viewMode = "grid" }) {
  const [hovered, setHovered] = useState(false);
  const canAccess = userCanAccess(session.tier);
  const catC = CAT_COLORS[session.category] || CAT_COLORS.Asana;
  const cap = capacity(session);
  const isLive = session.status === "live";
  const enrollPct = Math.round((session.enrolled / session.capacity) * 100);

  const getButtonStyle = () => {
    if (!canAccess) {
      return { background: "rgba(200,200,200,0.15)", color: T.muted, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "'Inter',sans-serif" };
    }
    if (isLive) {
      return { background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", border: "none", cursor: "pointer", boxShadow: `0 8px 20px rgba(239,68,68,0.4)`, fontFamily: "'Inter',sans-serif" };
    }
    if (session.status === "ended") {
      if (session.videoUrl) {
        return { background: `linear-gradient(135deg,${T.olive},${T.olive2})`, color: "#fff", border: "none", cursor: "pointer", boxShadow: `0 8px 20px rgba(107,138,90,0.38)`, fontFamily: "'Inter',sans-serif" };
      } else {
        return { background: "rgba(200,200,200,0.15)", color: T.light, border: `1px solid ${T.border}`, cursor: "default", fontFamily: "'Inter',sans-serif" };
      }
    }
    return {
      background: session.isCheckedIn ? "rgba(107,138,90,0.12)" : `linear-gradient(135deg,${T.orange},${T.orange2})`,
      color: session.isCheckedIn ? "#6B8A5A" : "#fff",
      border: session.isCheckedIn ? "1px solid rgba(107,138,90,0.25)" : "none",
      boxShadow: session.isCheckedIn ? "none" : `0 8px 20px rgba(242,154,12,0.38)`,
      cursor: "pointer",
      fontFamily: "'Inter',sans-serif"
    };
  };

  const handleClick = (e) => {
    if (e) e.stopPropagation();
    if (!canAccess) { onUpgrade(session); return; }
    if (session.status === "ended" && !session.videoUrl) return;
    onJoin(session);
  };

  if (viewMode === "list") {
    return (
      <motion.div initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }} transition={{ delay, duration: 0.45 }}
        onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)}
        className="flex items-center gap-5 rounded-[24px] p-5 cursor-pointer"
        style={{ ...glass, transition: "all 0.32s", transform: hovered ? "translateX(6px)" : "translateX(0)",
          boxShadow: hovered ? "0 24px 60px rgba(0,0,0,0.1)" : "0 12px 40px rgba(0,0,0,0.05)" }}
        onClick={handleClick}>

        {/* Category icon block */}
        <div className="w-14 h-14 rounded-[18px] flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg,${catC.from},${catC.to})`,
            boxShadow: `0 8px 20px ${catC.from}50` }}>
          {isLive
            ? <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}><FiRadio size={22} color="#fff" /></motion.div>
            : <FiVideo size={22} color="#fff" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {isLive && (
              <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-white px-2.5 py-1 rounded-full"
                style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)" }}>
                <FiRadio size={9} /> LIVE
              </motion.span>
            )}
            <h3 className="font-black text-[15px] text-[#1F1F1F] truncate"
              style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
              {session.title}
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[12px]" style={{ color: T.muted }}>{session.instructor}</span>
            <span className="flex items-center gap-1 text-[12px]" style={{ color: T.muted }}>
              <FiClock size={11} /> {session.duration} min
            </span>
            <span className="flex items-center gap-1 text-[12px]" style={{ color: T.muted }}>
              <FiCalendar size={11} /> {formatDate(session.start)} · {formatSessionTimeWithZone(session.start)}
            </span>
            <span className="flex items-center gap-1 text-[12px] font-semibold" style={{ color: cap.color }}>
              <FiUsers size={11} /> {session.enrolled}/{session.capacity}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <TierBadge tier={session.tier} />
          {session.status === "upcoming" && (
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: T.light }}>Starts in</div>
              <Countdown targetDate={session.start}
                style={{ fontSize: 13, fontWeight: 800, color: T.orange, fontFamily: "'Poppins',sans-serif" }} />
            </div>
          )}
          {canAccess && session.status === "upcoming" && session.isCheckedIn ? (
            <div className="flex items-center gap-2">
              <motion.button whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); onReschedule(session); }}
                className="h-9 px-4 rounded-full font-bold text-[11px] bg-zinc-800 text-white hover:bg-zinc-700 flex items-center gap-1.5"
                style={{ border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              >
                <FiRefreshCw size={11} /> Reschedule
              </motion.button>
              <motion.button whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); onCancelBooking(session); }}
                className="h-9 px-4 rounded-full font-bold text-[11px] bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500/15 flex items-center gap-1.5"
                style={{ cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              >
                <FiX size={11} /> Cancel
              </motion.button>
            </div>
          ) : (
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              className="h-10 px-5 rounded-full font-bold text-[13px] flex items-center gap-2"
              style={getButtonStyle()}
              onClick={handleClick}>
              {canAccess ? (
                isLive ? (
                  <><FiRadio size={12} /> Join</>
                ) : session.status === "ended" ? (
                  session.recorded ? (
                    <><FiPlay size={12} /> Replay</>
                  ) : (
                    <><FiVideoOff size={12} /> No Replay</>
                  )
                ) : (
                  session.isCheckedIn ? <><FiCheck size={12} /> Booked</> : <><FiCalendar size={12} /> Book Slot</>
                )
              ) : (
                <><FiLock size={12} /> Upgrade</>
              )}
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)}
      className="rounded-[28px] overflow-hidden cursor-pointer relative"
      style={{ ...glass, transition: "box-shadow 0.32s",
        boxShadow: hovered ? "0 28px 72px rgba(0,0,0,0.13)" : "0 16px 50px rgba(0,0,0,0.06)",
        transform: hovered ? "translateY(-10px)" : "translateY(0)",
        transitionProperty: "transform, box-shadow" }}
      onClick={handleClick}>

      {/* Top colour band */}
      <div className="relative overflow-hidden" style={{ height: 168,
        background: canAccess ? `linear-gradient(145deg,${catC.from},${catC.to})` : "linear-gradient(145deg,#C8C0B8,#B8B0A8)" }}>
        <div className="absolute inset-0 opacity-[0.08]"
          style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "18px 18px" }} />
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle,#fff,transparent)", filter: "blur(30px)", transform: "translate(30%,-30%)" }} />

        {/* Category / Live badge */}
        <div className="absolute top-4 left-4 flex gap-2">
          {isLive && (
            <motion.div animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[10px] font-black uppercase tracking-wider"
              style={{ background: "rgba(239,68,68,0.9)", backdropFilter: "blur(8px)" }}>
              <FiRadio size={9} /> Live Now
            </motion.div>
          )}
          <div className="px-3 py-1.5 rounded-full text-white text-[10px] font-bold"
            style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(8px)" }}>
            {session.category}
          </div>
        </div>

        {/* Lock overlay */}
        {!canAccess && (
          <div className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ background: "rgba(0,0,0,0.42)" }}>
            <FiLock size={28} color="#fff" style={{ marginBottom: 8 }} />
            <span className="text-white text-[11px] font-bold">
              Requires {TIERS[session.tier]?.label || TIERS[normalizeTier(session.tier)]?.label || session.tier || "Membership"}
            </span>
          </div>
        )}

        {/* Center icon */}
        {canAccess && (
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div animate={isLive ? { scale: [1, 1.15, 1] } : {}}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(10px)" }}>
              {isLive ? <FiRadio size={28} color="#fff" /> : <FiVideo size={28} color="#fff" />}
            </motion.div>
          </div>
        )}

        {/* Duration + recording */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {session.recorded && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-[9px] font-bold"
              style={{ background: "rgba(107,138,90,0.85)", backdropFilter: "blur(8px)" }}>
              <FiVideo size={9} /> Recorded
            </div>
          )}
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-[9px] font-bold"
            style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
            <FiClock size={9} /> {session.duration}m
          </div>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5">
        <h3 className="font-black text-[15px] text-[#1F1F1F] mb-1 leading-tight"
          style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>
          {session.title}
        </h3>
        <p className="text-[12px] mb-3" style={{ color: T.muted }}>{session.instructor}</p>
        <p className="text-[12px] leading-[1.65] mb-4" style={{ color: T.muted, display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {session.desc}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(session.tags || []).map(t => (
            <span key={t} className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
              style={{ background: "rgba(242,154,12,0.08)", color: "#C8660A" }}>{t}</span>
          ))}
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-[11px]" style={{ color: T.muted }}>
              <FiCalendar size={11} />{formatDate(session.start)}
            </div>
            <div className="flex items-center gap-1 text-[11px]" style={{ color: T.muted }}>
              <FiClock size={11} />{formatSessionTimeWithZone(session.start)}
            </div>
          </div>
          <TierBadge tier={session.tier} />
        </div>

        {/* Capacity bar */}
        <div className="mb-4">
          <div className="flex justify-between text-[10px] mb-1.5">
            <span className="font-semibold flex items-center gap-1" style={{ color: cap.color }}>
              <FiUsers size={10} /> {session.enrolled}/{session.capacity} enrolled
            </span>
            <span className="font-bold" style={{ color: cap.color }}>{cap.label}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(239,229,216,0.7)" }}>
            <motion.div className="h-full rounded-full" initial={{ width: 0 }}
              animate={{ width: `${enrollPct}%` }} transition={{ duration: 1, delay: delay + 0.3 }}
              style={{ background: enrollPct >= 90 ? "linear-gradient(90deg,#ef4444,#dc2626)" : enrollPct >= 70 ? `linear-gradient(90deg,${T.orange},${T.orange2})` : `linear-gradient(90deg,${T.olive},${T.olive2})` }} />
          </div>
        </div>

        {/* CTA */}
        {session.status === "ended" ? (
          <motion.button whileHover={session.videoUrl ? { y: -2 } : {}} whileTap={session.videoUrl ? { scale: 0.97 } : {}}
            style={getButtonStyle()}
            onClick={handleClick}
            className="w-full h-11 rounded-full font-bold text-[13px] flex items-center justify-center gap-2">
            {canAccess ? (
              session.videoUrl ? (
                <><FiPlay size={13} style={{ marginLeft: 2 }} /> Watch Replay</>
              ) : (
                <><FiVideoOff size={13} /> No Replay</>
              )
            ) : (
              <><FiLock size={13} /> Upgrade to watch</>
            )}
          </motion.button>
        ) : !canAccess ? (
          <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            onClick={handleClick}
            className="w-full h-11 rounded-full font-bold text-[13px] flex items-center justify-center gap-2"
            style={getButtonStyle()}>
            <FiLock size={13} /> Upgrade to {TIERS[session.tier]?.label || TIERS[normalizeTier(session.tier)]?.label || session.tier || "Membership"}
          </motion.button>
        ) : isLive ? (
          <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            onClick={handleClick}
            className="w-full h-11 rounded-full font-bold text-[13px] text-white flex items-center justify-center gap-2"
            style={getButtonStyle()}>
            <FiRadio size={13} /> Join Live Now
          </motion.button>
        ) : session.isCheckedIn ? (
          <div className="flex flex-col gap-2 w-full">
            <div className="flex gap-2">
              <motion.button whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); onReschedule(session); }}
                className="flex-1 h-9 rounded-full font-bold text-[11px] bg-zinc-800 text-white hover:bg-zinc-700 flex items-center justify-center gap-1.5"
                style={{ border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              >
                <FiRefreshCw size={11} /> Reschedule
              </motion.button>
              <motion.button whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); onCancelBooking(session); }}
                className="flex-1 h-9 rounded-full font-bold text-[11px] bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500/15 flex items-center justify-center gap-1.5"
                style={{ cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              >
                <FiX size={11} /> Cancel Slot
              </motion.button>
            </div>
            <div className="text-center text-[10px] text-[#6B8A5A] font-bold py-1.5 bg-[#6B8A5A]/10 rounded-lg border border-[#6B8A5A]/20">
              ✓ Slot Booked Successfully
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              onClick={handleClick}
              className="flex-1 h-11 rounded-full font-bold text-[13px] text-white flex items-center justify-center gap-2"
              style={getButtonStyle()}>
              <FiCalendar size={13} /> Book Slot
            </motion.button>
            <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(242,154,12,0.1)", border: `1px solid rgba(242,154,12,0.2)`, cursor: "pointer" }}>
              <FiBell size={14} style={{ color: T.orange }} />
            </motion.button>
          </div>
        )}

        {/* Countdown */}
        {session.status === "upcoming" && canAccess && (
          <div className="text-center mt-3 text-[11px]" style={{ color: T.light }}>
            Starts in{" "}
            <Countdown targetDate={session.start}
              style={{ fontWeight: 800, color: T.orange, fontFamily: "'Poppins',sans-serif" }} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── CALENDAR VIEW ────────────────────────────────────────────────────────────
function CalendarView({ sessions, onJoin, onUpgrade }) {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() + diffToMonday);
  weekStart.setHours(0, 0, 0, 0);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-[28px] overflow-hidden" style={glass}>
      {/* Day headers */}
      <div className="grid grid-cols-7" style={{ borderBottom: `1px solid ${T.border}` }}>
        {days.map((d, i) => {
          const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
          const isToday = d.getTime() === todayDate.getTime();
          return (
            <div key={i} className="py-4 text-center"
              style={{ borderRight: i < 6 ? `1px solid ${T.border}` : "none" }}>
              <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5"
                style={{ color: isToday ? T.orange : T.light }}>
                {WEEKDAYS[i]}
              </div>
              <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto font-black text-[15px]"
                style={isToday
                  ? { background: `linear-gradient(135deg,${T.orange},${T.orange2})`, color: "#fff",
                      boxShadow: `0 6px 16px rgba(242,154,12,0.4)`, fontFamily: "'Poppins',sans-serif" }
                  : { color: T.mid, fontFamily: "'Poppins',sans-serif" }}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Session slots */}
      <div className="grid grid-cols-7" style={{ minHeight: 220 }}>
        {days.map((d, di) => {
          const daySessions = sessions.filter(s => {
            const sd = new Date(s.start); sd.setHours(0, 0, 0, 0);
            const dd = new Date(d); dd.setHours(0, 0, 0, 0);
            return sd.getTime() === dd.getTime();
          });
          const canSee = (s) => userCanAccess(s.tier);

          return (
            <div key={di} className="p-2 flex flex-col gap-2"
              style={{ borderRight: di < 6 ? `1px solid ${T.border}` : "none", minHeight: 200 }}>
              {daySessions.map(s => {
                const catC = CAT_COLORS[s.category] || CAT_COLORS.Asana;
                const accessible = canSee(s);
                return (
                  <motion.div key={s.id} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                    onClick={() => accessible ? onJoin(s) : onUpgrade(s)}
                    title={`${s.title} - ${formatSessionTimeWithZone(s.start)}`}
                    className="rounded-[12px] p-2.5 cursor-pointer relative overflow-hidden"
                    style={{ background: accessible
                      ? `linear-gradient(145deg,${catC.from},${catC.to})`
                      : "rgba(200,200,200,0.2)",
                      boxShadow: accessible ? `0 4px 14px ${catC.from}40` : "none" }}>
                    {!accessible && (
                      <div className="absolute top-1 right-1">
                        <FiLock size={9} style={{ color: T.light }} />
                      </div>
                    )}
                    <div className="text-[10px] font-black truncate"
                      style={{ color: accessible ? "#fff" : T.light }}>
                      {s.title}
                    </div>
                    <div className="text-[9px] mt-0.5 opacity-80"
                      style={{ color: accessible ? "rgba(255,255,255,0.85)" : T.light }}>
                      {formatTime(s.start)}
                    </div>
                    {s.status === "live" && (
                      <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1, repeat: Infinity }}
                        className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── PAST SESSIONS ROW ────────────────────────────────────────────────────────
function PastSessionsRow({ sessions = [], onUpgrade, onJoin, viewMode }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState("");
  const itemsPerPage = 6;

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Find all dates with past sessions to point them out on the calendar
  const sessionDatesSet = useMemo(() => {
    const dates = new Set();
    sessions.forEach(s => {
      const sDate = new Date(s.start);
      const y = sDate.getFullYear();
      const m = String(sDate.getMonth() + 1).padStart(2, "0");
      const d = String(sDate.getDate()).padStart(2, "0");
      dates.add(`${y}-${m}-${d}`);
    });
    return dates;
  }, [sessions]);

  const handlePrevMonth = () => {
    setCurrentMonth(m => {
      if (m === 0) {
        setCurrentYear(y => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(m => {
      if (m === 11) {
        setCurrentYear(y => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const handleDayClick = (day) => {
    const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (selectedDate === formattedDate) {
      setSelectedDate(""); // Clear selection
    } else {
      setSelectedDate(formattedDate);
    }
  };

  const filteredSessions = useMemo(() => {
    if (!selectedDate) return sessions;
    return sessions.filter(s => {
      const sDate = new Date(s.start);
      const year = sDate.getFullYear();
      const month = String(sDate.getMonth() + 1).padStart(2, "0");
      const day = String(sDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      return dateStr === selectedDate;
    });
  }, [sessions, selectedDate]);

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredSessions.length]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} className="mt-8">
      
      {/* Title Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-black text-[22px] text-[#1F1F1F]"
            style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            Past Sessions Calendar & Replays
          </h2>
          <p className="text-[13px] mt-0.5" style={{ color: T.muted }}>
            Interactive calendar highlight: select any date with a dot to watch that day's class recordings
          </p>
        </div>

        {selectedDate && (
          <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            onClick={() => setSelectedDate("")}
            className="px-4 py-2 rounded-full text-[11px] font-bold bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-[#C8660A] hover:bg-[#F29A0C]/15 transition-all cursor-pointer"
          >
            Show All Completed Classes ({selectedDate}) ×
          </motion.button>
        )}
      </div>

      {/* Grid Dashboard Container - Flex Layout on Desktop */}
      <div className="flex flex-col lg:flex-row gap-6 items-start w-full">
        
        {/* Left Side: Replay Cards List (3 cards per row) */}
        <div className="flex-1 w-full order-2 lg:order-1">
          
          {/* List Header + Pagination row */}
          <div className="flex items-center justify-between mb-4 bg-zinc-50/50 p-3 rounded-2xl border border-zinc-100">
            <span className="text-[12px] font-semibold text-zinc-500">
              {selectedDate 
                ? `Showing completed classes on ${selectedDate}` 
                : "Showing all completed classes"} ({filteredSessions.length} session{filteredSessions.length !== 1 ? "s" : ""})
            </span>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <FiChevronLeft size={14} className="text-zinc-600" />
                </button>
                <span className="text-[11px] font-bold text-zinc-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <FiChevronRight size={14} className="text-zinc-600" />
                </button>
              </div>
            )}
          </div>

          {filteredSessions.length === 0 ? (
            <div className="text-center py-20 rounded-[28px] border border-dashed bg-white/40" style={{ borderColor: T.border }}>
              <FiVideo size={40} className="text-zinc-400 mb-3 mx-auto block" />
              <p className="font-bold text-[14px] text-zinc-700">No completed classes on this date</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Please choose a highlighted calendar date pointing with green indicators</p>
            </div>
          ) : (
            <div className={viewMode === "list" ? "flex flex-col gap-4" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"}>
              {paginatedSessions.map((s, i) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  delay={i * 0.05}
                  onJoin={onJoin}
                  onUpgrade={onUpgrade}
                  viewMode={viewMode === "calendar" ? "grid" : viewMode}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Side: Custom Month-Based Calendar */}
        <div className="w-full lg:w-[320px] p-5 rounded-[24px] border border-zinc-200 bg-white shadow-sm flex-shrink-0 order-1 lg:order-2">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <span className="font-black text-sm text-zinc-800" style={{ fontFamily: "'Poppins',sans-serif" }}>
              {MONTHS[currentMonth]} {currentYear}
            </span>
            <div className="flex gap-1">
              <button 
                onClick={handlePrevMonth}
                className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors cursor-pointer"
              >
                <FiChevronLeft size={13} />
              </button>
              <button 
                onClick={handleNextMonth}
                className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors cursor-pointer"
              >
                <FiChevronRight size={13} />
              </button>
            </div>
          </div>

          {/* Weekday Labels */}
          <div className="grid grid-cols-7 gap-1 mb-2 text-center text-[10px] font-black uppercase tracking-wider text-zinc-400">
            {DAYS.map(d => <div key={d}>{d}</div>)}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const hasSession = sessionDatesSet.has(formattedDate);
              const isSelected = selectedDate === formattedDate;

              return (
                <motion.button 
                  key={day}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  onClick={() => handleDayClick(day)}
                  className="relative w-full aspect-square rounded-xl flex items-center justify-center text-[11px] font-bold border-none cursor-pointer transition-all"
                  style={{
                    background: isSelected 
                      ? "linear-gradient(135deg,#6B8A5A,#8BA47A)" 
                      : hasSession 
                        ? "rgba(107,138,90,0.1)" 
                        : "transparent",
                    color: isSelected 
                      ? "#fff" 
                      : hasSession 
                        ? "#6B8A5A" 
                        : "#1F1F1F",
                    border: isSelected 
                      ? "none" 
                      : hasSession 
                        ? "1px solid rgba(107,138,90,0.2)" 
                        : "none",
                    boxShadow: isSelected ? "0 8px 16px rgba(107,138,90,0.3)" : "none",
                    fontFamily: "'Poppins',sans-serif"
                  }}
                >
                  {day}
                  {hasSession && (
                    <span 
                      className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
                      style={{ 
                        background: isSelected ? "#fff" : "#6B8A5A" 
                      }} 
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

      </div>
    </motion.div>
  );
}

// ─── ACTIVE STUDIO VIDEO BROADCAST PLAYER ─────────────────────────────────────
function ActiveStudio({ session, onClose }) {
  const localVideoRef = useRef(null);
  const [micMuted, setMicMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [activeStream, setActiveStream] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const navigate = useNavigate();

  const startMedia = async () => {
    try {
      setMediaError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setActiveStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      setCamOff(false);
      setMicMuted(false);
      return stream;
    } catch (err) {
      console.warn("Webcam/mic access blocked or not found:", err);
      setMediaError("Camera or Microphone access was denied or is blocked by your browser. You can still join the live session in listen/view mode.");
      setCamOff(true);
      setMicMuted(true);
      return null;
    }
  };

  useEffect(() => {
    let capturedStream = null;
    if (session.status === "live") {
      startMedia().then(stream => {
        capturedStream = stream;
      });
    }

    return () => {
      if (capturedStream) {
        capturedStream.getTracks().forEach(t => t.stop());
      }
      if (activeStream) {
        activeStream.getTracks().forEach(t => t.stop());
      }
    };
  }, [session.status]);

  const handleEnterSession = () => {
    if (activeStream) {
      activeStream.getTracks().forEach(t => t.stop());
    }
    navigate(`/live-room/${session.id}`);
  };

  const toggleMic = async () => {
    if (activeStream) {
      const audioTracks = activeStream.getAudioTracks();
      if (audioTracks.length > 0) {
        const nextState = !audioTracks[0].enabled;
        audioTracks.forEach(t => { t.enabled = nextState; });
        setMicMuted(!nextState);
      } else {
        await startMedia();
      }
    } else {
      await startMedia();
    }
  };

  const toggleCam = async () => {
    if (activeStream) {
      const videoTracks = activeStream.getVideoTracks();
      if (videoTracks.length > 0) {
        const nextState = !videoTracks[0].enabled;
        videoTracks.forEach(t => { t.enabled = nextState; });
        setCamOff(!nextState);
        if (nextState && localVideoRef.current) {
          localVideoRef.current.srcObject = activeStream;
        }
      } else {
        await startMedia();
      }
    } else {
      await startMedia();
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-20">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F4D9B5]/40">
        <div>
          <button 
            onClick={onClose}
            className="flex items-center gap-2 text-sm font-bold text-[#F29A0C] hover:text-[#E97818] bg-transparent border-none cursor-pointer"
          >
            ← Back to Sessions
          </button>
          <h2 className="text-[24px] font-black text-[#111] mt-2" style={{ fontFamily: "'Poppins',sans-serif" }}>
            {session.title}
          </h2>
          <p className="text-xs text-[#777]">with {session.instructor}</p>
        </div>
        <div className="flex items-center gap-2">
          {session.status === "live" ? (
            <span className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-white text-[11px] font-black bg-red-500 shadow-md">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> LIVE STUDIO
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#6B8A5A] text-[11px] font-black bg-[#FFF8EE] border border-[#F4D9B5]">
              RECORDED CLASS
            </span>
          )}
        </div>
      </div>

      <div className={session.status === "live" ? "grid lg:grid-cols-[1fr_340px] gap-6 items-start" : "max-w-4xl mx-auto space-y-6"}>
        <div className="rounded-[28px] overflow-hidden bg-black aspect-video relative flex items-center justify-center border border-[#F4D9B5]/30 shadow-2xl">
          {session.status === "ended" && session.videoUrl ? (
            <video 
              src={session.videoUrl} 
              controls 
              autoPlay 
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full relative flex items-center justify-center">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 p-6 text-center z-10">
                <GiLotus className="text-[64px] text-[#F29A0C] animate-spin-slow mb-4" />
                <p className="text-[22px] font-black" style={{ fontFamily: "'Poppins',sans-serif" }}>
                  {session.status === "live" ? "Live Interactive Studio" : "Self-Hosted Streaming Studio"}
                </p>
                <p className="text-[13px] text-white/75 mt-1.5 max-w-md leading-relaxed">
                  {session.status === "live" 
                    ? "Interactive video and audio channels are ready. Check your media controls on the right and click below to enter the live classroom." 
                    : "Audio/video feed is active. Session is scheduled to start."}
                </p>
                {session.status === "live" && (
                  <motion.button 
                    onClick={handleEnterSession}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    className="mt-6 px-9 py-3.5 rounded-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-extrabold text-[13px] tracking-wider uppercase shadow-[0_12px_28px_rgba(239,68,68,0.45)] inline-flex items-center gap-2.5 border-none outline-none cursor-pointer transition-all"
                  >
                    <FiRadio size={16} className="animate-pulse" /> Enter Live Interactive Session
                  </motion.button>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/90" />
            </div>
          )}

          {/* Student Local Live Preview Box */}
          {session.status === "live" && (
            <div className="absolute bottom-5 right-5 w-[150px] h-[100px] md:w-[190px] md:h-[125px] rounded-[18px] overflow-hidden bg-zinc-900 border border-white/25 shadow-2xl z-20 flex items-center justify-center">
              {camOff ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-white/60 text-[10.5px] gap-1.5 bg-zinc-950 p-2 text-center">
                  <FiVideoOff size={18} className="text-amber-400" />
                  <span className="font-semibold">Camera Off</span>
                </div>
              ) : (
                <div className="relative w-full h-full">
                  <video 
                    ref={localVideoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover scale-x-[-1]"
                  />
                  <div className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-full bg-black/75 text-[9px] font-bold text-white/90 backdrop-blur-sm">
                    Live Preview
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {session.status === "live" ? (
          <div className="rounded-[24px] border border-[#F4D9B5] bg-white p-5 shadow-lg space-y-4">
            <div>
              <h4 className="text-[16px] font-black text-[#111]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                Device & Studio Controls
              </h4>
              <p className="text-[11.5px] text-[#777] mt-0.5 font-medium">Configure camera and microphone before joining</p>
            </div>

            {/* Media Toggles */}
            <div className="grid grid-cols-2 gap-2.5">
              <button 
                onClick={toggleMic}
                className={`py-3 px-3 rounded-2xl font-bold text-[11.5px] flex items-center justify-center gap-2 transition-all border cursor-pointer ${
                  micMuted 
                    ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" 
                    : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shadow-sm"
                }`}
              >
                {micMuted ? <FiMicOff size={15} className="text-red-500" /> : <FiMic size={15} className="text-emerald-600" />}
                <span>{micMuted ? "Mic Muted" : "Mic Active"}</span>
              </button>
              <button 
                onClick={toggleCam}
                className={`py-3 px-3 rounded-2xl font-bold text-[11.5px] flex items-center justify-center gap-2 transition-all border cursor-pointer ${
                  camOff 
                    ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" 
                    : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shadow-sm"
                }`}
              >
                {camOff ? <FiVideoOff size={15} className="text-red-500" /> : <FiVideo size={15} className="text-emerald-600" />}
                <span>{camOff ? "Camera Off" : "Camera Active"}</span>
              </button>
            </div>

            {mediaError && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed flex items-start gap-2">
                <FiInfo className="text-amber-600 flex-shrink-0 mt-0.5" size={14} />
                <span>{mediaError}</span>
              </div>
            )}

            {/* Privacy & Recording Notice */}
            <div className="rounded-[18px] bg-[#FFF8EE] border border-[#F4D9B5] p-3.5 space-y-2">
              <div className="flex items-center gap-1.5 text-[#B85204] font-extrabold text-[12px]">
                <FiShield size={14} className="text-[#E97818]" />
                <span>Recording & Privacy Policy</span>
              </div>
              <div className="text-[11px] text-[#555] leading-relaxed space-y-1.5 font-medium">
                <p className="flex items-start gap-1.5">
                  <FiCheckCircle className="text-emerald-600 flex-shrink-0 mt-0.5" size={12} />
                  <span><strong>Admin/Instructor Screen Only:</strong> Only the instructor's screen broadcast and teaching are recorded for official student class replays.</span>
                </p>
                <p className="flex items-start gap-1.5">
                  <FiCheckCircle className="text-emerald-600 flex-shrink-0 mt-0.5" size={12} />
                  <span><strong>No User Screen Recorded:</strong> Your personal screen and private desktop will never be recorded.</span>
                </p>
                <p className="flex items-start gap-1.5">
                  <FiCheckCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={12} />
                  <span><strong>Interactive Feeds:</strong> Camera & mic allow real-time posture corrections by the yoga master and can be toggled anytime.</span>
                </p>
              </div>
            </div>

            <div className="border-t border-[#F4D9B5]/40 pt-3 space-y-1.5">
              <h5 className="text-[12px] font-black text-[#111]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                Class Details
              </h5>
              <p className="text-[11.5px] leading-relaxed text-[#666] line-clamp-2">{session.desc || "Daily guided live yoga practice session."}</p>
            </div>

            <button 
              onClick={handleEnterSession}
              className="w-full py-3.5 rounded-full font-bold text-xs bg-red-600 text-white shadow-lg border-none hover:bg-red-700 transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <FiRadio size={14} /> Join Classroom Now
            </button>

            <button 
              onClick={onClose}
              className="w-full py-2.5 rounded-full font-semibold text-[11.5px] bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors border-none cursor-pointer"
            >
              Cancel & Return
            </button>
          </div>
        ) : (
          <div className="rounded-[24px] border border-[#F4D9B5] bg-white p-6 shadow-lg space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                <FiPlay size={18} className="text-green-600" />
              </div>
              <div>
                <h4 className="text-[15px] font-black text-[#111]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                  Class Details & Description
                </h4>
                <p className="text-[11px] text-[#777] mt-0.5">Recorded class replay session</p>
              </div>
            </div>
            <p className="text-[13px] leading-relaxed text-[#444] bg-[#FFF8EE]/60 p-4 rounded-2xl border border-[#F4D9B5]/30">
              {session.desc || "No description provided for this session."}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function LiveSessionsPage() {
  const { packages: dynamicPackages, tiers: dynamicTiers } = useDynamicPackages();
  CACHED_PACKAGES = dynamicPackages;
  const { triggerCheckInRestriction } = useCheckIn();
  const [viewMode, setViewMode] = useState("grid");
  const [category, setCategory] = useState("All");
  const [instructor, setInstructor] = useState("All Instructors");
  const [tierFilter, setTierFilter] = useState("All Tiers");
  const dynamicTierOptions = useMemo(() => ["All Tiers", ...dynamicTiers], [dynamicTiers]);
  const [search, setSearch] = useState("");
  const [activeModal, setActiveModal] = useState(null); // { type: "join"|"upgrade", session }
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTier, setUserTier] = useState("Free");
  const [userRank, setUserRank] = useState(0);
  const [rescheduleSession, setRescheduleSession] = useState(null); // Session user wants to reschedule FROM
  const [replaySession, setReplaySession] = useState(null); // Session user wants to watch replay of
  const [confirmCancelSession, setConfirmCancelSession] = useState(null); // Session user wants to cancel booking of

  const normaliseSession = (s) => {
    const hasRealVideo = s.videoUrl && s.videoUrl.trim() !== "" && !s.videoUrl.startsWith("/live-room");
    return {
      id: s.id,
      title: s.title,
      instructor: s.instructorName || "Instructor TBA",
      start: parseUtcDate(s.scheduledAt),
      duration: s.durationInMinutes || 60,
      category: s.category || "Asana",
      tier: s.tier || "Free",
      enrolled: s.enrolledCount || 0,
      capacity: s.maxCapacity || 30,
      status: s.isLive ? "live" : (s.isUpcoming ? "upcoming" : "ended"),
      videoUrl: s.videoUrl,
      isCheckedIn: s.isCheckedIn || false,
      desc: s.description || "",
      tags: s.tags || [],
      recorded: !!hasRealVideo
    };
  };

  const fetchSessions = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await liveSessionAPI.getSessions();
      if (res.data?.success) {
        const list = (res.data.data || []).map(normaliseSession);
        setSessions(list);
        const tier = res.data.userTier || "Free";
        const rank = typeof res.data.userRank === "number" ? res.data.userRank : getTierRank(tier, dynamicPackages);
        setUserTier(tier);
        setUserRank(rank);
        CURRENT_USER_TIER = tier;
        CURRENT_USER_RANK = rank;
      }
    } catch (err) {
      console.error("Failed to fetch sessions", err);
      if (!silent) toast.error("Failed to load live sessions.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (dynamicPackages && dynamicPackages.length > 0) {
      CACHED_PACKAGES = dynamicPackages;
    }
  }, [dynamicPackages]);

  useEffect(() => {
    fetchSessions();

    userAPI.getProfile().then(profileRes => {
      if (profileRes.data?.data) {
        const pTier = profileRes.data.data.tier || profileRes.data.data.activeSubscription?.packageName || "Free";
        const pRank = profileRes.data.data.userRank ?? profileRes.data.data.accessLevel ?? profileRes.data.data.activeSubscription?.accessLevel ?? getTierRank(pTier, dynamicPackages);
        setUserTier(pTier);
        setUserRank(pRank);
        CURRENT_USER_TIER = pTier;
        CURRENT_USER_RANK = pRank;
      }
    }).catch(() => {});

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5154';
    const connection = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hubs/livesession`)
      .withAutomaticReconnect()
      .build();

    connection.on("SessionUpdated", () => {
      fetchSessions(true); // silent dynamic refresh
    });

    connection.start()
      .then(() => console.log("SignalR Connected!"))
      .catch(err => console.error("SignalR Connection Failed: ", err));

    return () => {
      connection.stop();
    };
  }, []);

  const nextLive = sessions.find(s => s.status === "live") ||
                   sessions.filter(s => s.status === "upcoming").sort((a, b) => a.start - b.start)[0];

  const openJoin    = (s) => {
    triggerCheckInRestriction(async () => {
      if (s.status === "ended") {
        if (s.recorded) {
          setReplaySession(s);
        } else {
          toast.info("This session does not have a recorded replay available.");
        }
      } else {
        if (s.isCheckedIn) {
          if (s.status === "live") {
            setActiveSession(s);
          } else {
            // Open countdown screen since it is an upcoming session!
            setActiveModal({ type: "countdown", session: s });
          }
        } else {
          try {
            const locStr = localStorage.getItem("last_geolocation") || "Unknown";
            const res = await liveSessionAPI.checkIn(s.id, locStr);
            if (res.data?.success) {
              toast.success(s.status === "live" ? "Entered session successfully!" : "Slot booked successfully!");
              if (s.status === "live") {
                setActiveSession(s);
              } else {
                // If it was checked in (slot booked) successfully, open the countdown modal!
                setActiveModal({ type: "countdown", session: s });
              }
              fetchSessions();
            }
          } catch (err) {
            toast.error(err.response?.data?.message || "Failed to book slot");
          }
        }
      }
    });
  };
  const openUpgrade = (s) => setActiveModal({ type: "upgrade", session: s });
  const closeModal  = ()  => setActiveModal(null);
  
  const uniqueInstructors = ["All Instructors", ...new Set(sessions.map(s => s.instructor).filter(Boolean))];

  const filtered = sessions.filter(s => {
    const mc = category === "All" || s.category === category;
    const mi = instructor === "All Instructors" || s.instructor === instructor;
    const mt = tierFilter === "All Tiers" || s.tier === tierFilter;
    const ms = s.title.toLowerCase().includes(search.toLowerCase()) ||
               s.instructor.toLowerCase().includes(search.toLowerCase());
    return mc && mi && mt && ms;
  });

  const liveNow  = filtered.filter(s => s.status === "live");
  const upcoming = filtered.filter(s => s.status === "upcoming");
  const ended    = filtered.filter(s => s.status === "ended");



  const handleCancelConfirm = async () => {
    if (!confirmCancelSession) return;
    try {
      const res = await liveSessionAPI.cancelBooking(confirmCancelSession.id);
      if (res.data?.success) {
        toast.success("Booking cancelled successfully!");
        fetchSessions();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel booking.");
    } finally {
      setConfirmCancelSession(null);
    }
  };

  const handleRescheduleConfirm = async (toSessionId) => {
    try {
      const res = await liveSessionAPI.rescheduleBooking({
        fromSessionId: rescheduleSession.id,
        toSessionId: toSessionId
      });
      if (res.data?.success) {
        toast.success("Class rescheduled successfully!");
        fetchSessions();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reschedule booking.");
    } finally {
      setRescheduleSession(null);
    }
  };

  const upcomingCount = sessions.filter(s => s.status === "upcoming").length;
  const attendedCount = sessions.filter(s => s.isCheckedIn).length;
  const totalMinutes = sessions.filter(s => s.isCheckedIn).reduce((acc, s) => acc + s.duration, 0);
  const totalHoursStr = `${Math.round((totalMinutes / 60) * 10) / 10}h`;
  const completedSessions = sessions.filter(s => s.status === "ended").length;
  const attendanceDenominator = completedSessions > 0 ? completedSessions : sessions.length;
  const attendanceRateVal = (attendedCount === 0 || attendanceDenominator === 0)
    ? 0
    : Math.min(100, Math.round((attendedCount / attendanceDenominator) * 100));
  const attendanceRateStr = `${attendanceRateVal}%`;

  if (activeSession) {
    return (
      <UserDashboardLayout>
        <div className="w-full min-w-0 py-6" style={{ fontFamily: "'Inter',sans-serif" }}>
          <ActiveStudio session={activeSession} onClose={() => setActiveSession(null)} />
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="w-full min-w-0" style={{ fontFamily: "'Inter',sans-serif" }}>
        {loading ? (
          <BrandLoader 
            message="Loading Live Sessions..." 
            subMessage="Syncing studio schedule and class broadcasts" 
          />
        ) : (
          <div className="relative z-10">
            {/* Ambient orbs */}
            <div className="fixed pointer-events-none z-0"
              style={{ width: 500, height: 500, borderRadius: "50%",
                background: "radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)",
                filter: "blur(80px)", top: -150, right: -100 }} />
            <div className="fixed pointer-events-none z-0"
              style={{ width: 400, height: 400, borderRadius: "50%",
                background: "radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",
                filter: "blur(80px)", bottom: -100, left: -80 }} />

            <Topbar/>
          {/* ── HERO ── */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-[36px] p-6 sm:p-8 md:p-10 mb-6"
            style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)", backgroundSize: "22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width: 300, height: 300, background: "radial-gradient(circle,rgba(242,154,12,0.1),transparent)", filter: "blur(40px)" }} />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 2, repeat: Infinity }}
                    className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", boxShadow: "0 8px 22px rgba(239,68,68,0.5)" }}>
                    <FiRadio size={20} color="#fff" />
                  </motion.div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color: "#ef4444" }}>Live Sessions</p>
                    <p className="text-[12px]" style={{ color: T.light }}>Real-time yoga with expert instructors</p>
                  </div>
                  {liveNow.length > 0 && (
                    <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                      className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full text-white text-[11px] font-black"
                      style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", boxShadow: "0 6px 18px rgba(239,68,68,0.45)" }}>
                      <FiRadio size={11} /> {liveNow.length} Session{liveNow.length !== 1 ? "s" : ""} Live Now
                    </motion.span>
                  )}
                </div>

                <h1 className="font-black mb-4 text-[26px] sm:text-[34px] lg:text-[40px]"
                  style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-1.5px", lineHeight: 1.06, color: T.dark }}>
                  Train live,{" "}
                  <span style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    feel the energy.
                  </span>
                </h1>
                <p className="text-[15px] leading-[1.8] mb-7" style={{ color: T.muted, maxWidth: 480 }}>
                  Join expert-led live yoga sessions in real time. Check in, practise together, ask questions, and track every session on your journey.
                </p>

                {/* Search */}
                <div className="flex items-center gap-3 max-w-md rounded-[16px] px-4 py-3"
                  style={{ background: "rgba(255,255,255,0.85)", border: `1px solid ${T.border}` }}>
                  <FiSearch style={{ color: T.orange, fontSize: 16, flexShrink: 0 }} />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search sessions, instructors…"
                    className="flex-1 bg-transparent outline-none text-sm text-[#1F1F1F] placeholder-[#CCC]"
                    style={{ fontFamily: "'Inter',sans-serif" }} />
                </div>
              </div>

              {/* Next live countdown card */}
              {nextLive ? (
                <TiltCard intensity={5}
                  className="rounded-[28px] overflow-hidden"
                  style={{
                    background: nextLive.status === "live" ? "linear-gradient(145deg,#ef4444,#dc2626)" : "linear-gradient(145deg,#F29A0C,#E97818)",
                    boxShadow: nextLive.status === "live" ? "0 24px 60px rgba(239,68,68,0.45)" : "0 24px 60px rgba(242,154,12,0.35)"
                  }}>
                  <div className="p-7 text-white relative">
                    <div className="absolute inset-0 opacity-[0.07]"
                      style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "16px 16px" }} />
                    <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
                      style={{ background: "radial-gradient(circle,#fff,transparent)", filter: "blur(30px)", transform: "translate(30%,-30%)" }} />
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                        <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider">
                          <div className="w-2 h-2 rounded-full bg-white" />
                          {nextLive.status === "live" ? "Live Now" : "Upcoming Class"}
                        </motion.div>
                        {nextLive.status === "upcoming" && (
                          <span className="text-[11px] font-bold bg-white/20 px-2.5 py-1 rounded-full flex-shrink-0">
                            Starts in <Countdown targetDate={nextLive.start} style={{ fontWeight: 800 }} />
                          </span>
                        )}
                      </div>
                      <h3 className="font-black text-[20px] mb-1 truncate" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                        {nextLive.title}
                      </h3>
                      <p className="text-white/70 text-[12px] mb-6">{nextLive.instructor} · {nextLive.duration} min</p>

                      <div className="grid grid-cols-3 gap-3 mb-6">
                        {["Enrolled", "Capacity", "Duration"].map((label, i) => (
                          <div key={label} className="rounded-[14px] p-3 text-center"
                            style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}>
                            <div className="font-black text-[20px]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                              {i === 0 ? nextLive.enrolled : i === 1 ? nextLive.capacity : `${nextLive.duration}m`}
                            </div>
                            <div className="text-[9px] text-white/65 font-bold uppercase tracking-wide mt-0.5">{label}</div>
                          </div>
                        ))}
                      </div>

                      {nextLive.status === "live" ? (
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                          onClick={() => openJoin(nextLive)}
                          className="w-full h-12 rounded-full font-black text-[14px] flex items-center justify-center gap-2"
                          style={{ background: "rgba(255,255,255,0.95)", color: "#dc2626",
                            border: "none", cursor: "pointer", fontFamily: "'Inter',sans-serif",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}>
                          <FiRadio size={15} /> Enter Live Studio
                        </motion.button>
                      ) : (
                        <motion.button whileHover={{ scale: nextLive.isCheckedIn ? 1 : 1.02 }} whileTap={{ scale: nextLive.isCheckedIn ? 1 : 0.97 }}
                          onClick={() => {
                            if (nextLive.isCheckedIn) return;
                            if (userCanAccess(nextLive.tier)) {
                              openJoin(nextLive);
                            } else {
                              openUpgrade(nextLive);
                            }
                          }}
                          className="w-full h-12 rounded-full font-black text-[14px] flex items-center justify-center gap-2 transition-all"
                          style={{
                            background: nextLive.isCheckedIn ? "rgba(255,255,255,0.2)" : "#fff",
                            color: nextLive.isCheckedIn ? "#fff" : "#E97818",
                            border: nextLive.isCheckedIn ? "1px solid rgba(255,255,255,0.3)" : "none",
                            cursor: nextLive.isCheckedIn ? "default" : "pointer",
                            fontFamily: "'Inter',sans-serif",
                            boxShadow: nextLive.isCheckedIn ? "none" : "0 8px 24px rgba(0,0,0,0.15)"
                          }}>
                          {nextLive.isCheckedIn ? (
                            <><FiCheck size={15} /> Slot Booked</>
                          ) : (
                            <><FiCalendar size={15} /> Book Slot Now</>
                          )}
                        </motion.button>
                      )}
                    </div>
                  </div>
                </TiltCard>
              ) : (
                <div className="rounded-[28px] p-8 text-center flex flex-col items-center justify-center min-h-[220px]"
                  style={{ ...glass, border: "1px dashed rgba(242,154,12,0.3)" }}>
                  <GiLotus className="text-[48px] text-[#F29A0C] mb-3 animate-pulse" />
                  <h3 className="font-bold text-[16px] text-gray-800" style={{ fontFamily: "'Poppins',sans-serif" }}>No Scheduled Sessions</h3>
                  <p className="text-xs text-gray-500 mt-1 max-w-[220px]">Check back later for live streaming and interactive batch classes.</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* ── STATS STRIP ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="ud-kpi grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 mb-6 rounded-[24px] overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.9)", boxShadow: "0 12px 40px rgba(0,0,0,0.05)" }}>
            {[
              { icon: FiCalendar, val: String(upcomingCount),   label: "Upcoming Sessions",   sub: "Scheduled classes",    accent: T.orange, bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon: FiCheckCircle, val: String(attendedCount), label: "Sessions Attended",  sub: "Live checked in",      accent: T.olive,  bg: "linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon: FiClock,    val: totalHoursStr,          label: "Live Studio Hours",   sub: "Live class time only", accent: T.orange, bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon: FiAward,    val: attendanceRateStr,      label: "Attendance Rate",     sub: "Completion rate",      accent: T.olive,  bg: "linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
            ].map((s, i) => (
              <motion.div key={s.label} whileHover={{ background: "rgba(255,255,255,0.95)" }}
                className="relative overflow-hidden px-6 py-5 flex items-center gap-4"
                style={{ background: s.bg, borderRight: i < 3 ? `1px solid rgba(239,229,216,0.5)` : "none" }}>
                <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                  style={{ background: `linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                <div className="absolute top-0 right-0 w-20 h-20 rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle,${s.accent}18,transparent)`, filter: "blur(16px)", transform: "translate(30%,-30%)" }} />
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow: `0 6px 16px ${s.accent}40` }}>
                  <s.icon size={16} color="#fff" />
                </div>
                <div className="min-w-0">
                  <p className="ud-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 26, letterSpacing: "-1px", lineHeight: 1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl text-[10px] font-bold uppercase tracking-[0.7px] mt-0.5 truncate" style={{ color: T.light }}>{s.label}</p>
                  {s.sub && (
                    <p className="ud-kpi-sub text-[10px] font-semibold mt-0.5 truncate" style={{ color: T.mid }}>
                      {s.sub}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── FILTER BAR ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
            className="flex items-center gap-3 mb-6 flex-wrap">

            {/* Category pills */}
            <div className="ud-scroll flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <motion.button key={cat} onClick={() => setCategory(cat)} whileTap={{ scale: 0.94 }}
                  className="px-4 py-2 rounded-full text-[12px] font-bold border-0 outline-none cursor-pointer"
                  style={category === cat
                    ? { background: `linear-gradient(135deg,${T.orange},${T.orange2})`, color: "#fff",
                        boxShadow: `0 6px 18px rgba(242,154,12,0.38)`, fontFamily: "'Inter',sans-serif" }
                    : { background: "rgba(255,255,255,0.82)", color: T.muted,
                        border: `1px solid ${T.border}`, fontFamily: "'Inter',sans-serif" }
                  }>
                  {cat}
                </motion.button>
              ))}
            </div>

            {/* Instructor dropdown */}
            <CustomDropdown
              value={instructor}
              onChange={setInstructor}
              options={uniqueInstructors}
              label="Instructor"
              icon={FiUser}
              fullWidth={false}
            />

            {/* Tier filter */}
            <CustomDropdown
              value={tierFilter}
              onChange={setTierFilter}
              options={dynamicTierOptions}
              label="Plan"
              icon={FiTag}
              fullWidth={false}
            />

            {/* View toggle */}
            <div className="ml-auto flex rounded-[14px] overflow-hidden"
              style={{ border: `1px solid ${T.border}` }}>
              {[["grid", FiGrid], ["list", FiList], ["calendar", FiCalendar]].map(([mode, Icon]) => (
                <motion.button key={mode} onClick={() => setViewMode(mode)} whileTap={{ scale: 0.92 }}
                  className="w-10 h-9 flex items-center justify-center border-0 outline-none cursor-pointer"
                  style={{ background: viewMode === mode ? `linear-gradient(135deg,${T.orange},${T.orange2})` : "rgba(255,255,255,0.82)" }}>
                  <Icon size={14} style={{ color: viewMode === mode ? "#fff" : T.muted }} />
                </motion.button>
              ))}
            </div>

            <span className="text-[12px] font-semibold" style={{ color: T.light }}>
              {filtered.length} session{filtered.length !== 1 ? "s" : ""}
            </span>
          </motion.div>

          {/* ── CALENDAR VIEW ── */}
          <AnimatePresence mode="wait">
            {viewMode === "calendar" && (
              <motion.div key="calendar" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }} className="mb-8">
                <CalendarView sessions={sessions} onJoin={openJoin} onUpgrade={openUpgrade} />
              </motion.div>
            )}

            {/* ── GRID / LIST VIEW ── */}
            {viewMode !== "calendar" && (
              <motion.div key={viewMode} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

                {/* Live Now */}
                {liveNow.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                        className="w-2.5 h-2.5 rounded-full" style={{ background: "#ef4444" }} />
                      <h2 className="font-black text-[22px] text-[#1F1F1F]"
                        style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                        Live Now
                      </h2>
                    </div>
                    <div className={viewMode === "grid" ? "grid gap-5" : "flex flex-col gap-4"}
                      style={viewMode === "grid" ? { gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))" } : {}}>
                      {liveNow.map((s, i) => (
                        <SessionCard key={s.id} session={s} delay={i * 0.06}
                          onJoin={openJoin} onUpgrade={openUpgrade} onCancelBooking={setConfirmCancelSession} onReschedule={setRescheduleSession} viewMode={viewMode} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming */}
                {upcoming.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="font-black text-[22px] text-[#1F1F1F]"
                          style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                          Upcoming Sessions
                        </h2>
                        <p className="text-[13px] mt-0.5" style={{ color: T.muted }}>
                          Your tier: <strong style={{ color: T.orange }}>{(TIERS[userTier] || TIERS[normalizeTier(userTier)])?.label || userTier}</strong>   sessions above your tier require an upgrade
                        </p>
                      </div>
                    </div>
                    <div className={viewMode === "grid" ? "grid gap-5" : "flex flex-col gap-4"}
                      style={viewMode === "grid" ? { gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))" } : {}}>
                      {upcoming.map((s, i) => (
                        <SessionCard key={s.id} session={s} delay={i * 0.05}
                          onJoin={openJoin} onUpgrade={openUpgrade} onCancelBooking={setConfirmCancelSession} onReschedule={setRescheduleSession} viewMode={viewMode} />
                      ))}
                    </div>
                  </div>
                )}

                {filtered.length === 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                    <FiCalendar size={48} style={{ color: T.light, marginBottom: 16, display: "block", margin: "0 auto 16px" }} />
                    <p className="font-bold text-[18px] text-[#1F1F1F] mb-2"
                      style={{ fontFamily: "'Poppins',sans-serif" }}>No sessions found</p>
                    <p className="text-[14px]" style={{ color: T.muted }}>Try adjusting your filters</p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── PAST SESSIONS ── */}
          <PastSessionsRow sessions={ended} onUpgrade={openUpgrade} onJoin={openJoin} viewMode={viewMode} />

        </div>
        )}
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {activeModal?.type === "upgrade" && (
          <UpgradeModal session={activeModal.session} onClose={closeModal} packages={dynamicPackages} />
        )}
        {activeModal?.type === "countdown" && (
          <CountdownModal 
            session={activeModal.session} 
            onClose={closeModal} 
            onStart={() => {
              closeModal();
              setActiveSession(activeModal.session);
            }} 
          />
        )}

        {rescheduleSession && (
          <RescheduleModal session={rescheduleSession} sessions={sessions} onClose={() => setRescheduleSession(null)} onConfirm={handleRescheduleConfirm} />
        )}
        {replaySession && (
          <ReplayPlayerModal session={replaySession} onClose={() => setReplaySession(null)} />
        )}
        {confirmCancelSession && (
          <CancelConfirmModal session={confirmCancelSession} onClose={() => setConfirmCancelSession(null)} onConfirm={handleCancelConfirm} />
        )}
      </AnimatePresence>

    </UserDashboardLayout>
  );
}